/* External dependencies */
import React, { useCallback, useContext, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";

/* Internal dependencies */
import { Button, useToggleState } from "@yoast/ui-library";
import * as styles from "./styles.module.scss";
import SelectOrAddSite from "./SelectOrAddSite";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import { SubscriptionDetailContext } from "../../context/SubscriptionDetailContext";
import ErrorDisplay from "../../errors/ErrorDisplay";

/**
 * A form for adding a site to the user's account.
 *
 * @returns {Element} The ConnectSiteToSubscription form.
 */
export const ConnectSiteToSubscription = injectIntl( ( { onSuccess } ) => {
	const subscriptionContext                          = useContext( SubscriptionDetailContext );
	const [ selectedURL, setSelectedURL ]              = useState( null );
	const [ isLoading, , , startLoading, stopLoading ] = useToggleState( false );
	const [ connectSiteError, setConnectSiteError ]    = useState( null );
	const canSubmit                                    = !! selectedURL;
	const submit = useCallback( async( event ) => {
		event.preventDefault();
		if ( ! canSubmit || isLoading ) {
			return;
		}

		try {
			startLoading();
			setConnectSiteError( null );
			await doRequest( prepareInternalRequest(
				`Subscriptions/${ subscriptionContext.subscriptionId }/sites`,
				"POST",
				{ url: selectedURL.href },
			) );
		} catch ( error ) {
			setConnectSiteError( { type: "error", message: error.message || "Something went wrong. Please try again later." } );
			return;
		} finally {
			stopLoading();
		}

		subscriptionContext.request.reload();

		if ( onSuccess ) {
			onSuccess();
		}
	}, [ subscriptionContext.subscriptionId, canSubmit, selectedURL, onSuccess ] );

	return (
		<div id="add-site-modal">
			<form onSubmit={ submit } className={ styles.form }>
				<SelectOrAddSite onSiteUrlChange={ setSelectedURL } />
				<div>
					<Button
						id="add-site-modal-submit-button"
						type="submit"
						isLoading={ isLoading }
						disabled={ ! canSubmit }
						aria-disabled={ ! canSubmit }
					>
						<FormattedMessage id="sites.addSite.connect" defaultMessage="Add site" />
					</Button>
				</div>
				<ErrorDisplay error={ connectSiteError } />
			</form>
		</div>
	);
} );

export default ConnectSiteToSubscription;
